import React, { useMemo, useEffect, useState } from "react"
import {
  Container,
  CardBody,
  Card,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Badge,
} from "reactstrap"
import Breadcrumb from "components/Common/Breadcrumb"
import { Link, useNavigate } from "react-router-dom"
import { app_url } from "app_config"
import { deleteNow } from "helpers/delete_function"
import { axiosbaseurl } from "configure"
import Loader from "components/Common/Loader"
import { setUpdatedData } from "helpers/data_setters"
import { usePermissionData } from "AppContext"
import PermittedComponent from "components/Common/PermittedComponent"
import CommonTable from "components/Common/CommonTable"
import swal from "sweetalert"
import secureLocalStorage from "react-secure-storage"
import moment from "moment"

const AllMeetings = () => {
  const [table_data, settable_data] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const getMeetings = async () => {
      try {
        setLoading(true)
        const response = await axiosbaseurl.get("/meeting")
        if (response.data.success) {
          settable_data(response.data.data.reverse() || [])
        }
      } catch (error) {
        console.log(error)
        swal("Error", "Failed to load meetings", "error")
      } finally {
        setLoading(false)
      }
    }
    getMeetings()
  }, [])

  document.title = "Minutes of Meeting | Peacock Accountants Limited"

  return (
    <>
      {loading && <Loader />}

      <div className="page-content mb-5">
        <Container fluid>
          <Breadcrumb title="Dashboard" breadcrumbItem="Minutes of Meeting" />

          <Card className="px-0 mx-0 shadow-lg">
            <CardBody>
              <TableComponent
                table_data={table_data}
                settable_data={settable_data}
              />
            </CardBody>
          </Card>
        </Container>
      </div>
    </>
  )
}

const TableComponent = ({ table_data, settable_data }) => {
  const navigate = useNavigate()
  const { checkAccess, permissions } = usePermissionData()
  const module_id = 30

  const loggedInUser = secureLocalStorage.getItem("authUser")

  // Modal state
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedMeeting, setSelectedMeeting] = useState(null)

  // Open modal with attendees
  const openAttendeesModal = meeting => {
    setSelectedMeeting(meeting)
    setModalOpen(true)
  }

  const handleDelete = async id => {
    try {
      const response = await axiosbaseurl.delete(`/meeting/${id}`)
      if (response.data.success) {
        settable_data(prev =>
          setUpdatedData(prev, response.data.data, "delete")
        )
        swal("Success", "Meeting deleted successfully", "success")
      }
    } catch (error) {
      console.log(error)
      swal(
        "Error",
        error.response?.data?.message || "Something went wrong",
        "error"
      )
    }
  }

  // Helper: safely parse a JSON array field and normalise all values to strings
  const parseMeetingArray = raw => {
    try {
      const parsed = JSON.parse(raw || "[]")
      return Array.isArray(parsed) ? parsed.map(String) : []
    } catch {
      return []
    }
  }

  const handleAccept = async meetingId => {
    const userId = loggedInUser?.employeeId
    if (!userId) {
      swal("Error", "User not logged in", "error")
      return
    }

    try {
      const currentMeeting = table_data.find(m => m.id === meetingId)
      if (!currentMeeting) return

      // Normalise to strings so comparison is always reliable
      const acceptedBy = parseMeetingArray(currentMeeting.accepted)
      const declinedBy = parseMeetingArray(currentMeeting.declined)

      if (acceptedBy.includes(String(userId))) {
        swal("Info", "You have already confirmed attendance", "info")
        return
      }

      // Add user to accepted; remove from declined if present
      const newAccepted = [...acceptedBy, String(userId)]
      const newDeclined = declinedBy.filter(id => id !== String(userId))

      const response = await axiosbaseurl.put(`/meeting/${meetingId}/accept`, {
        accepted: JSON.stringify(newAccepted),
        declined: JSON.stringify(newDeclined),
      })

      if (response.data.success) {
        settable_data(prev =>
          prev.map(meeting =>
            meeting.id === meetingId
              ? {
                  ...meeting,
                  accepted: JSON.stringify(newAccepted),
                  declined: JSON.stringify(newDeclined),
                }
              : meeting
          )
        )
        swal("Success", "Attendance confirmed", "success")
      }
    } catch (error) {
      console.error("Accept failed:", error)
      swal(
        "Error",
        error.response?.data?.message || "Failed to confirm attendance",
        "error"
      )
    }
  }

  const handleDecline = async meetingId => {
    const userId = loggedInUser?.employeeId
    if (!userId) {
      swal("Error", "User not logged in", "error")
      return
    }

    try {
      const currentMeeting = table_data.find(m => m.id === meetingId)
      if (!currentMeeting) return

      // Normalise to strings so comparison is always reliable
      const acceptedBy = parseMeetingArray(currentMeeting.accepted)
      const declinedBy = parseMeetingArray(currentMeeting.declined)

      if (declinedBy.includes(String(userId))) {
        swal("Info", "You have already declined this meeting", "info")
        return
      }

      // Add user to declined; remove from accepted if present
      const newDeclined = [...declinedBy, String(userId)]
      const newAccepted = acceptedBy.filter(id => id !== String(userId))

      const response = await axiosbaseurl.put(`/meeting/${meetingId}/decline`, {
        declined: JSON.stringify(newDeclined),
        accepted: JSON.stringify(newAccepted),
      })

      if (response.data.success) {
        settable_data(prev =>
          prev.map(meeting =>
            meeting.id === meetingId
              ? {
                  ...meeting,
                  declined: JSON.stringify(newDeclined),
                  accepted: JSON.stringify(newAccepted),
                  attendeesWithNames: (meeting.attendeesWithNames || []).filter(
                    emp => String(emp.id) !== String(userId)
                  ),
                }
              : meeting
          )
        )
        swal("Success", "Meeting Declined", "success")
      }
    } catch (error) {
      console.log(error)
      swal(
        "Error",
        error.response?.data?.message || "Failed to decline",
        "error"
      )
    }
  }

  const calculateTotalTime = (fromTime, toTime) => {
    if (!fromTime || !toTime) return "00:00"

    const [fromH, fromM] = fromTime.split(":").map(Number)
    const [toH, toM] = toTime.split(":").map(Number)

    let diffMinutes = toH * 60 + toM - (fromH * 60 + fromM)
    if (diffMinutes < 0) diffMinutes += 24 * 60

    const hours = Math.floor(diffMinutes / 60)
    const minutes = diffMinutes % 60

    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}`
  }

  const columns = useMemo(
    () => [
      {
        Header: "ID",
        accessor: "id",
        disableFilters: true,
        className: "text-center",
        Cell: ({ row }) => <span>{row.index + 1}</span>,
      },
      {
        Header: "Title",
        accessor: "meetingTitle",
        disableFilters: true,
      },
      {
        Header: "Date",
        accessor: "createdAt",
        disableFilters: true,
        className: "text-center",
        Cell: ({ row }) => (
          <span>
            {new Date(row.original.createdAt).toLocaleDateString("en-GB")}
          </span>
        ),
      },
      {
        Header: "Timing",
        disableFilters: true,
        className: "text-center",
        Cell: ({ row }) => {
          const { fromTime, toTime } = row.original
          return (
            <span>
              {fromTime} - {toTime}
            </span>
          )
        },
      },
      {
        Header: "Duration",
        disableFilters: true,
        className: "text-center",
        Cell: ({ row }) => {
          const { fromTime, toTime } = row.original
          return <span>{calculateTotalTime(fromTime, toTime)} hrs</span>
        },
      },
      {
        Header: "Attendees",
        disableFilters: true,
        disableSortBy: true,
        className: "text-center",
        Cell: ({ row }) => {
          const attendees = row.original.attendeesWithNames || []
          const count = attendees.length

          return count > 0 ? (
            <button
              onClick={() => openAttendeesModal(row.original)}
              className="btn btn-info btn-sm"
            >
              <i className="fas fa-users me-1"></i>
              {count} {count === 1 ? "Person" : "People"}
            </button>
          ) : (
            <span className="text-muted">—</span>
          )
        },
      },
      {
        Header: "Action",
        disableFilters: true,
        disableSortBy: true,
        className: "text-center",
        Cell: ({ row }) => {
          const meeting = row.original
          const attendees = meeting.attendeesWithNames || []
          const userId = loggedInUser?.employeeId

          const isAttendee = attendees.some(emp => emp.id == userId)

          const presentDay =
            moment(meeting.createdAt).format("YYYY-MM-DD") ===
            moment().format("YYYY-MM-DD")

          // Normalise arrays to strings for reliable comparison
          const acceptedArr = parseMeetingArray(meeting.accepted)
          const declinedArr = parseMeetingArray(meeting.declined)

          const hasAccepted = acceptedArr.includes(String(userId))
          const hasDeclined = declinedArr.includes(String(userId))

          return (
            <div className="d-flex justify-content-center gap-2 flex-wrap">
              {isAttendee && !hasDeclined && (
                <button
                  onClick={() => handleAccept(meeting.id)}
                  className={`btn btn-sm fw-semibold ${
                    hasAccepted ? "btn-outline-success" : "btn-success"
                  }`}
                  disabled={hasAccepted}
                  title={
                    hasAccepted
                      ? "Attendance already confirmed"
                      : "Confirm attendance"
                  }
                >
                  <i className="fas fa-check"></i>
                  {hasAccepted ? " Accepted" : " Accept"}
                </button>
              )}

              {!hasAccepted && isAttendee && (
                <button
                  onClick={() => handleDecline(meeting.id)}
                  className={`btn btn-sm fw-semibold ${
                    hasDeclined ? "btn-outline-danger" : "btn-danger"
                  }`}
                  disabled={hasDeclined}
                  title={hasDeclined ? "Already declined" : "Decline"}
                >
                  <i className="fas fa-ban"></i>
                  {hasDeclined ? " Declined" : " Decline"}
                </button>
              )}

              {checkAccess(module_id, "edit") && isAttendee && presentDay && (
                <Link
                  to={`${app_url}edit-meeting/${meeting.id}`}
                  className="btn btn-success bg-green btn-sm"
                >
                  <i className="fas fa-pen me-1"></i> Edit
                </Link>
              )}

              {(!presentDay || !isAttendee) && (
                <Link
                  to={`${app_url}edit-meeting/${meeting.id}`}
                  className="btn btn-primary btn-sm"
                >
                  <i className="fas fa-eye me-1"></i> View
                </Link>
              )}

              {checkAccess(module_id, "remove") && (
                <button
                  onClick={() => deleteNow(() => handleDelete(meeting.id))}
                  className={`btn btn-danger btn-sm ${
                    acceptedArr.length ? "d-none" : ""
                  }`}
                >
                  <i className="fas fa-trash-alt"></i>
                </button>
              )}
            </div>
          )
        },
      },
    ],
    [table_data, permissions]
  )

  const data = useMemo(() => table_data, [table_data])

  return (
    <>
      <PermittedComponent isAccess={checkAccess(module_id, "view")}>
        <CommonTable columns={columns} data={data} heading="Meetings">
          <div>
            {checkAccess(module_id, "add") && (
              <Link
                to={`${app_url}add-meeting`}
                className="btn btn-primary bg-blue ms-3"
              >
                <i className="fas fa-plus me-2"></i> Add Meeting
              </Link>
            )}
            <button
              onClick={() => navigate(-1)}
              className="btn btn-secondary bg-dark ms-3"
            >
              <i className="fas fa-arrow-left me-3"></i> Back
            </button>
          </div>
        </CommonTable>
      </PermittedComponent>

      {/* Attendees Modal */}
      <Modal
        isOpen={modalOpen}
        toggle={() => setModalOpen(false)}
        centered
        size="md"
      >
        <ModalHeader toggle={() => setModalOpen(false)}>
          Attendees - {selectedMeeting?.meetingTitle}
        </ModalHeader>
        <ModalBody>
          {selectedMeeting?.attendeesWithNames?.length > 0 ? (
            <div className="d-flex flex-wrap gap-3">
              {selectedMeeting.attendeesWithNames.map(emp => (
                <Badge
                  key={emp.id}
                  pill
                  color="primary"
                  className="px-4 py-2 font-size-14"
                >
                  {emp.name} {emp.lastName && emp.lastName}
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-muted text-center mb-0">
              No attendees assigned to this meeting.
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <button
            className="btn btn-secondary"
            onClick={() => setModalOpen(false)}
          >
            Close
          </button>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default AllMeetings
